import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import './Toast.css';
import { supabase } from '../supabaseClient';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [toasts, setToasts] = useState([]);
  const [toastsEnabled, setToastsEnabled] = useState(() => {
    return localStorage.getItem('gdt_toasts_enabled') !== 'false';
  });

  const toggleToasts = useCallback(() => {
    setToastsEnabled(prev => {
      const next = !prev;
      localStorage.setItem('gdt_toasts_enabled', String(next));
      return next;
    });
  }, []);

  // Fetch initial notifications
  useEffect(() => {
    const loadInitial = async () => {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);
      
      if (data) {
        setNotifications(data.map(n => ({
          id: n.id,
          title: n.title,
          message: n.description || n.title,
          type: n.type || 'info',
          time: new Date(n.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          read: n.read || false
        })));
        setUnreadCount(data.filter(n => !n.read).length);
      }
    };

    loadInitial();

    // SUBSCRIBE TO REALTIME - Using a specific channel name
    const channel = supabase
      .channel('notification_updates')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications' },
        (payload) => {
          console.log('🔔 REALTIME NOTIFICATION RECEIVED:', payload);
          const newN = payload.new;
          const toastNotif = {
            id: newN.id,
            title: newN.title,
            message: newN.description || newN.title,
            type: newN.type || 'info',
            time: new Date(newN.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            read: false
          };
          
          setNotifications(prev => [toastNotif, ...prev].slice(0, 50));
          setUnreadCount(prev => prev + 1);

          if (localStorage.getItem('gdt_toasts_enabled') !== 'false') {
            setToasts(prev => [...prev, toastNotif]);
            setTimeout(() => {
              setToasts(prev => prev.filter(t => t.id !== toastNotif.id));
            }, 5000);
          }
        }
      )
      .subscribe((status) => {
        console.log('📡 Realtime subscription status:', status);
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const addNotification = useCallback((message, type = 'info') => {
    const id = Date.now();
    // Support both string message and object {title, message} for backward compatibility
    let text = typeof message === 'string' ? message : (message.message || message.title || 'Notificación');
    
    const newNotification = {
      id,
      title: typeof message === 'string' ? 'Notificación' : message.title,
      message: text,
      type,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      read: false
    };
    
    setNotifications(prev => [newNotification, ...prev].slice(0, 50));
    setUnreadCount(prev => prev + 1);
    
    // Add to toasts if enabled
    if (localStorage.getItem('gdt_toasts_enabled') !== 'false') {
      setToasts(prev => [...prev, newNotification]);
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== id));
      }, 3000);
    }
  }, []);

  const markAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  return (
    <NotificationContext.Provider value={{ 
      notifications, 
      unreadCount, 
      toastsEnabled,
      toggleToasts,
      addNotification, 
      markAsRead, 
      clearNotifications 
    }}>
      {children}
      {toastsEnabled && (
        <div className="toast-container">
          {toasts.map(t => (
            <div key={t.id} className={`toast toast-${t.type}`}>
              {t.message}
            </div>
          ))}
        </div>
      )}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
